import { useMemo, useRef, useContext, useEffect, useState, useCallback } from 'react'

import { Box, FormHelperText } from '@material-ui/core'

import ContentModalContext from '../ContentModalProvider/Context'
import { RULES } from './validation-rules'

interface IValidationMessageProps {
  valuesByError: {
    [key: string]: any
  }
  touched: boolean
  label: string
  [key: string]: any
}

const ValidationMessage = ({ valuesByError, touched, label, ...rest }: IValidationMessageProps) => {
  const { validation } = useContext(ContentModalContext)
  const { errors, hasErrors } = validation.calculateErrors(valuesByError)

  const lastReported = useRef<boolean>()
  useEffect(() => {
    if (lastReported.current !== hasErrors) {
      if (hasErrors) {
        validation.setError(label, errors)
      } else {
        validation.setError(label, undefined)
      }
      lastReported.current = hasErrors
    }
  }, [validation, label, hasErrors, errors])

  if (!touched) return null

  return (
    <>
      {errors.map((error: string, idx: number) => {
        return (
          <FormHelperText
            key={idx + error}
            {...rest}
            component="div"
            error
          >
            <Box px={2}>{error}</Box>
          </FormHelperText>
        )
      })}
    </>
  )
}

export default ValidationMessage

interface IErrorsObj {
  [key: string]: string | undefined
}

export const useValidation = () => {
  const [errors, setErrors] = useState<IErrorsObj>({})

  const getError = useCallback(
    (field: string) => {
      return errors[field]
    },
    [errors],
  )

  const getHasError = useCallback(
    (field: string) => {
      return !!errors[field]
    },
    [errors],
  )

  const setError = useCallback((field: string, error: string | undefined) => {
    setErrors((prevErrors) => ({
      ...prevErrors,
      [field]: error,
    }))
  }, [])

  const clearErrors = useCallback((fieldNames) => {
    setErrors((prevErrors) => {
      const updated = {
        ...prevErrors,
      }
      fieldNames.forEach((fieldName: string) => {
        updated[fieldName] = undefined
      })
      return updated
    })
  }, [])

  const calculateErrors = useCallback((valuesByError: { [key: string]: any }) => {
    const errors = Object.keys(valuesByError).reduce((acc: string[], error: string): string[] => {
      const value = valuesByError[error]
      const hasError = RULES[error](value)
      if (hasError) {
        acc.push(error)
      }
      return acc
    }, [] as string[])
    return {
      errors,
      hasErrors: errors.length > 0,
    }
  }, [])

  const hasErrors = useMemo(() => {
    const values = Object.values(errors)
    return values.some((error) => !!error)
  }, [errors])

  return {
    errors,
    hasErrors,
    getError,
    clearErrors,
    getHasError,
    calculateErrors,
    setError,
  }
}
